/**
 * @fileoverview InfiniaModal Component - Shows information about Infinia.ie
 * 
 * Displays a modal with details about what Infinia does when users click
 * "Crafted by Infinia.ie" in the footer.
 * 
 * @component
 * @example
 * <InfiniaModal isOpen={true} onClose={() => setIsOpen(false)} />
 * 
 * @requires React
 * @requires ./InfiniaModal.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 30/07/2025
 */

import React from 'react';
import './InfiniaModal.css';

/**
 * InfiniaModal Component
 * 
 * Shows information about Infinia.ie services and capabilities
 * 
 * @component
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Callback function to close the modal
 * @returns {JSX.Element} Modal with Infinia information
 */
function InfiniaModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const services = [
    {
      icon: "fas fa-globe",
      title: "Web Portals & Applications",
      description: "Enterprise-grade React portals built in days, not months"
    },
    {
      icon: "fas fa-brain",
      title: "AI Integration",
      description: "Custom AI chatbots, RAG systems, and vector databases"
    },
    {
      icon: "fas fa-code",
      title: "Full Stack Development", 
      description: "Modern tech stacks with multi-cloud infrastructure"
    },
    {
      icon: "fas fa-shield-alt",
      title: "Enterprise Solutions",
      description: "Digital transformation and enterprise architecture"
    }
  ];

  return (
    <div className="infinia-modal-overlay" onClick={handleOverlayClick}>
      <div className="infinia-modal-content">
        {/* Modal Header */}
        <div className="infinia-modal-header">
          <div className="infinia-logo-section">
            <div className="infinia-logo">
              <img src="/images/infinia.png" alt="Infinia Logo" className="infinia-logo-img" />
              <span>INFINIA</span>
            </div>
            <p className="infinia-tagline">Elite Development Without Enterprise Bureaucracy</p>
          </div>
          <button className="infinia-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="infinia-modal-body">
          <div className="infinia-intro">
            <h2>What We Do</h2>
            <p>
              Founded by John Bellew with <strong>30+ years of enterprise experience</strong>, 
              Infinia delivers cutting-edge B2B solutions for government, finance, and law firms. 
              We've built systems serving <strong>20,000+ professionals</strong> with a 100% project delivery rate.
            </p>
          </div>

          <div className="infinia-services">
            <h3>Our Core Services</h3>
            <div className="services-grid">
              {services.map((service, index) => (
                <div key={index} className="service-card">
                  <div className="service-icon">
                    <i className={service.icon}></i>
                  </div>
                  <h4>{service.title}</h4>
                  <p>{service.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="infinia-highlights">
            <div className="highlight">
              <i className="fas fa-award"></i>
              <div>
                <h4>40+ Years</h4>
                <p>Combined Experience</p>
              </div>
            </div>
            <div className="highlight">
              <i className="fas fa-users"></i>
              <div>
                <h4>20,000+ Users</h4>
                <p>Systems in Production</p>
              </div>
            </div>
            <div className="highlight">
              <i className="fas fa-target"></i>
              <div>
                <h4>100% Success</h4>
                <p>Project Delivery Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="infinia-modal-footer">
          <div className="infinia-contact">
            <p>
              <strong>Interested in working with us?</strong> <br/> Visit infinia.ie or email hello@infinia.ie
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InfiniaModal;